// A search box whose results open under it (the APG combobox pattern with a listbox popup). The caller
// supplies an async `search(term, { signal })` returning `{ results, more }`, and what picking a result does.
// Styles: .me-search-combobox / .me-search-results / .me-search-option in z5-me-components.css.

import React, { useEffect, useId, useRef, useState } from 'react'
import PropTypes from 'prop-types'

// search-as-you-type suggestions (the table's 900ms R6.11 rule is for committing a table query)
export const SUGGEST_DEBOUNCE_MS = 300

export const MeSearchCombobox = ({
  label,
  placeholder,
  search,
  onSelect,
  renderOption,
  optionKey,
  minLength = 2,
  debounceMs = SUGGEST_DEBOUNCE_MS,
  autoFocus = false,
  large = false,
  moreText = 'More matches: keep typing to narrow it down',
  noResultsText = 'No matches',
}) => {
  const [term, setTerm] = useState('')
  const [status, setStatus] = useState('idle') // idle | loading | ready | error
  const [results, setResults] = useState([])
  const [more, setMore] = useState(false)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const timer = useRef(null)
  const latest = useRef(0)
  const controller = useRef(null)
  const inputRef = useRef(null)
  const listboxId = useId()
  const optionPrefix = useId()

  useEffect(
    () => () => {
      clearTimeout(timer.current)
      if (controller.current) controller.current.abort()
    },
    []
  )

  const run = value => {
    clearTimeout(timer.current)
    const query = value.trim()
    if (query.length < minLength) return
    const id = ++latest.current
    if (controller.current) controller.current.abort()
    controller.current =
      typeof AbortController !== 'undefined' ? new AbortController() : null
    setStatus('loading')
    setOpen(true)
    Promise.resolve(
      search(query, { signal: controller.current && controller.current.signal })
    )
      .then(response => {
        // a slower, older request never replaces newer results
        if (id !== latest.current) return
        setResults(response.results || [])
        setMore(Boolean(response.more))
        setActive(-1)
        setStatus('ready')
      })
      .catch(() => {
        if (id !== latest.current) return
        setResults([])
        setStatus('error')
      })
  }

  const onChange = event => {
    const value = event.target.value
    setTerm(value)
    clearTimeout(timer.current)
    if (value.trim().length < minLength) {
      latest.current += 1
      setResults([])
      setStatus('idle')
      setOpen(false)
      return
    }
    timer.current = setTimeout(() => run(value), debounceMs)
  }

  const choose = result => {
    setOpen(false)
    onSelect(result)
  }

  const onKeyDown = event => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (!open && results.length) setOpen(true)
        if (results.length) setActive(i => (i + 1) % results.length)
        break
      case 'ArrowUp':
        event.preventDefault()
        if (!open && results.length) setOpen(true)
        if (results.length)
          setActive(i => (i <= 0 ? results.length - 1 : i - 1))
        break
      case 'Enter':
        event.preventDefault()
        if (open && active >= 0 && results[active]) choose(results[active])
        else run(term)
        break
      case 'Escape':
        event.preventDefault()
        if (open) {
          setOpen(false)
          setActive(-1)
        } else {
          setTerm('')
          setResults([])
          setStatus('idle')
        }
        break
      default:
    }
  }

  const countText =
    status === 'loading'
      ? 'Searching…'
      : status === 'error'
      ? 'Search failed. Try again.'
      : status === 'ready'
      ? results.length
        ? `${results.length}${more ? '+' : ''} result${
            results.length === 1 && !more ? '' : 's'
          }`
        : noResultsText
      : ''

  const activeId = open && active >= 0 ? `${optionPrefix}-${active}` : undefined

  return (
    <div className="me-search-combobox">
      <span className={`me-search-field${large ? ' me-search-field-lg' : ''}`}>
        <i className="fa-regular fa-magnifying-glass" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={listboxId}
          aria-activedescendant={activeId}
          autoComplete="off"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={term}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setOpen(false)}
        />
      </span>
      <div
        data-me-menu=""
        className={`me-menu-surface me-search-results${open ? ' is-open' : ''}`}
      >
        <ul id={listboxId} role="listbox" aria-label={`${label} results`}>
          {results.map((result, i) => (
            <li
              key={optionKey(result)}
              id={`${optionPrefix}-${i}`}
              role="option"
              aria-selected={i === active ? 'true' : 'false'}
              className={`me-search-option${i === active ? ' is-active' : ''}`}
              // mousedown, so the pick lands before the input's blur closes the list
              onMouseDown={event => {
                event.preventDefault()
                choose(result)
              }}
              onMouseEnter={() => setActive(i)}
            >
              {renderOption(result)}
            </li>
          ))}
        </ul>
        {status !== 'ready' || !results.length || more ? (
          <p className="me-search-status">
            {status === 'ready' && results.length && more
              ? moreText
              : countText}
          </p>
        ) : null}
      </div>
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {countText}
      </span>
    </div>
  )
}

MeSearchCombobox.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  search: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  renderOption: PropTypes.func.isRequired,
  optionKey: PropTypes.func.isRequired,
  minLength: PropTypes.number,
  debounceMs: PropTypes.number,
  autoFocus: PropTypes.bool,
  large: PropTypes.bool,
  moreText: PropTypes.string,
  noResultsText: PropTypes.string,
}
